import React, { useState, useEffect } from 'react';
import './App.css';
import {
  INITIAL_BOARD,
  INITIAL_MOVED_PIECES,
  isWhitePiece,
  isValidMove,
  wouldBeInCheck,
  makeMove,
  getGameState,
  isKingInCheck,
  PIECES,
  isPawnPromotion
} from './gameLogic';

function App() {
  const [board, setBoard] = useState(INITIAL_BOARD);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [isWhiteTurn, setIsWhiteTurn] = useState(true);
  const [gameState, setGameState] = useState({ state: 'playing', winner: null });
  const [errorMessage, setErrorMessage] = useState(null);
  const [legalMovePieces, setLegalMovePieces] = useState([]);
  const [movedPieces, setMovedPieces] = useState(INITIAL_MOVED_PIECES);
  const [promotionPending, setPromotionPending] = useState(null);
  const [moveHistory, setMoveHistory] = useState([]);

  // Check game state after each move
  useEffect(() => {
    const currentGameState = getGameState(board, isWhiteTurn);
    setGameState(currentGameState);
    // Clear error message on new game state
    setErrorMessage(null);
    
    // If in check, find pieces that can block check
    if (currentGameState.state === 'check') {
      const pieces = [];
      for (let i = 0; i < 8; i++) {
        for (let j = 0; j < 8; j++) {
          const piece = board[i][j];
          if (piece && isWhitePiece(piece) === isWhiteTurn) {
            for (let x = 0; x < 8; x++) {
              for (let y = 0; y < 8; y++) {
                if (isValidMove(board, i, j, x, y, movedPieces) && 
                    !wouldBeInCheck(board, i, j, x, y, movedPieces)) {
                  pieces.push([i, j]);
                  break;
                }
              }
            }
          }
        }
      }
      setLegalMovePieces(pieces);
    } else {
      setLegalMovePieces([]);
    }
  }, [board, isWhiteTurn, movedPieces]);

  // Helper function to convert board coordinates to algebraic notation
  const toAlgebraic = (row, col) => {
    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    const ranks = ['8', '7', '6', '5', '4', '3', '2', '1'];
    return files[col] + ranks[row];
  };

  // Helper function to get piece name for notation
  const getPieceName = (piece) => {
    switch (piece) {
      case '♔': case '♚': return 'K';
      case '♕': case '♛': return 'Q';
      case '♖': case '♜': return 'R';
      case '♗': case '♝': return 'B';
      case '♘': case '♞': return 'N';
      default: return '';  // Pawns don't have a letter in algebraic notation
    }
  };

  // Create notation for a move
  const createMoveNotation = (piece, startRow, startCol, endRow, endCol, isCapture, promotionPiece = null) => {
    const pieceName = getPieceName(piece);
    const start = toAlgebraic(startRow, startCol);
    const end = toAlgebraic(endRow, endCol);
    
    let notation = pieceName;
    
    // For castling
    if (pieceName === 'K' && Math.abs(startCol - endCol) === 2) {
      return endCol > startCol ? 'O-O' : 'O-O-O';
    }
    
    // If it's a pawn and a capture, add the file of the pawn
    if (pieceName === '' && isCapture) {
      notation += start[0];
    }
    
    // Add capture symbol
    if (isCapture) {
      notation += 'x';
    }
    
    notation += end;
    
    // Add promotion piece
    if (promotionPiece) {
      const promotionMap = {
        'queen': 'Q',
        'rook': 'R',
        'bishop': 'B',
        'knight': 'N'
      };
      notation += '=' + promotionMap[promotionPiece];
    }
    
    return notation;
  };

  const handlePromotion = (pieceType) => {
    if (!promotionPending) return;

    const { startRow, startCol, endRow, endCol } = promotionPending;
    
    const { newBoard, newMovedPieces } = makeMove(
      board, 
      startRow, 
      startCol, 
      endRow, 
      endCol, 
      movedPieces,
      pieceType
    );
    
    // Update move history with promotion
    const piece = board[startRow][startCol];
    const isCapture = board[endRow][endCol] !== '';
    const notation = createMoveNotation(piece, startRow, startCol, endRow, endCol, isCapture, pieceType);
    setMoveHistory([...moveHistory, notation]);
    
    setBoard(newBoard);
    setMovedPieces(newMovedPieces);
    setPromotionPending(null);
    setSelectedPiece(null);
    setIsWhiteTurn(!isWhiteTurn);
  };

  const handleSquareClick = (i, j) => {
    if (gameState.state === 'checkmate' || gameState.state === 'stalemate') {
      return;
    }
    
    if (promotionPending) {
      return;
    }
    
    if (selectedPiece === null) {
      const piece = board[i][j];
      
      if (piece && isWhitePiece(piece) === isWhiteTurn) {
        if (gameState.state === 'check') {
          if (!legalMovePieces.some(([row, col]) => row === i && col === j)) {
            setErrorMessage(`This ${piece} cannot block the check!`);
            return;
          } else {
            setErrorMessage(null);
          }
        }
        
        setSelectedPiece({ row: i, col: j });
      }
    } 
    else {
      if (i === selectedPiece.row && j === selectedPiece.col) {
        setSelectedPiece(null);
      } else if (
        isValidMove(board, selectedPiece.row, selectedPiece.col, i, j, movedPieces) &&
        !wouldBeInCheck(board, selectedPiece.row, selectedPiece.col, i, j, movedPieces)
      ) {
        if (isPawnPromotion(board, selectedPiece.row, selectedPiece.col, i)) {
          setPromotionPending({
            startRow: selectedPiece.row,
            startCol: selectedPiece.col,
            endRow: i,
            endCol: j
          });
          return;
        }
        
        const piece = board[selectedPiece.row][selectedPiece.col];
        const isCapture = board[i][j] !== '';
        
        const { newBoard, newMovedPieces } = makeMove(
          board, 
          selectedPiece.row, 
          selectedPiece.col, 
          i, 
          j, 
          movedPieces
        );
        
        // Create and add move notation to history
        const notation = createMoveNotation(piece, selectedPiece.row, selectedPiece.col, i, j, isCapture);
        setMoveHistory([...moveHistory, notation]);
        
        setBoard(newBoard);
        setMovedPieces(newMovedPieces);
        setSelectedPiece(null);
        setIsWhiteTurn(!isWhiteTurn);
      } else if (isValidMove(board, selectedPiece.row, selectedPiece.col, i, j, movedPieces)) {
        setErrorMessage("This move doesn't block the check!");
        setSelectedPiece(null);
      } else {
        // Move is invalid
      }
    }
  };

  const renderSquare = (i, j) => {
    const isBlack = (i + j) % 2 === 1;
    const isSelected = selectedPiece?.row === i && selectedPiece?.col === j;
    
    return (
      <div 
        className={`square ${isBlack ? 'black' : 'white'} 
                    ${isSelected ? 'selected' : ''}`}
        key={`${i}-${j}`}
        onClick={() => handleSquareClick(i, j)}
      >
        <div className="piece">
          {board[i][j]}
        </div>
      </div>
    );
  };

  // Function to check if a square should be highlighted as a castling option - no longer used
  const showCastlingSquare = (row, col) => {
    // Keeping the function but returning false always to disable the highlighting
    return false;
  };

  const renderBoard = () => {
    const board = [];
    for (let i = 0; i < 8; i++) {
      const row = [];
      for (let j = 0; j < 8; j++) {
        row.push(renderSquare(i, j));
      }
      board.push(
        <div className="board-row" key={i}>
          {row}
        </div>
      );
    }
    return board;
  };

  const resetGame = () => {
    setBoard(INITIAL_BOARD);
    setSelectedPiece(null);
    setIsWhiteTurn(true);
    setGameState({ state: 'playing', winner: null });
    setErrorMessage(null);
    setLegalMovePieces([]);
    setMovedPieces(INITIAL_MOVED_PIECES);
    setPromotionPending(null);
    setMoveHistory([]);  // Reset move history
  };

  const renderPromotionOptions = () => {
    if (!promotionPending) return null;
    
    const isWhite = isWhiteTurn;
    const pieces = [
      { type: 'queen', symbol: isWhite ? '♕' : '♛' },
      { type: 'rook', symbol: isWhite ? '♖' : '♜' },
      { type: 'bishop', symbol: isWhite ? '♗' : '♝' },
      { type: 'knight', symbol: isWhite ? '♘' : '♞' }
    ];
    
    return (
      <div className="promotion-modal">
        <div className="promotion-content">
          <h3>Choose promotion piece:</h3>
          <div className="promotion-options">
            {pieces.map(piece => (
              <div
                key={piece.type}
                className="promotion-piece"
                onClick={() => handlePromotion(piece.type)}
              >
                {piece.symbol}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render the move history panel
  const renderMoveHistory = () => {
    // Group moves into pairs for white and black
    const pairs = [];
    for (let i = 0; i < moveHistory.length; i += 2) {
      pairs.push({
        number: Math.floor(i / 2) + 1,
        white: moveHistory[i],
        black: moveHistory[i + 1] || ''
      });
    }
    
    return (
      <div className="move-history">
        <h3>Move History</h3>
        <div className="move-list">
          {pairs.map((pair, index) => (
            <div key={index} className="move-pair">
              <span className="move-number">{pair.number}.</span>
              <span className="white-move">{pair.white}</span>
              <span className="black-move">{pair.black}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      <div className="game-container">
        <div className="chessboard">
          {renderBoard()}
        </div>
        <div className="side-panel">
          <div className="error-container">
            {errorMessage && (
              <div className="error-message">
                {errorMessage}
              </div>
            )}
          </div>
          {gameState.state === 'playing' ? (
            <div className={`game-info ${isWhiteTurn ? 'white-turn' : 'black-turn'}`}>
              {isWhiteTurn ? "White's turn" : "Black's turn"}
            </div>
          ) : gameState.state === 'check' ? (
            <div className="game-info check">
              Check!
            </div>
          ) : gameState.state === 'stalemate' ? (
            <div className="game-info stalemate">
              Stalemate - Draw!
            </div>
          ) : (
            <div className="game-info game-over">
              {gameState.winner === 'white' ? 'White' : 'Black'} wins!
            </div>
          )}
          
          {renderMoveHistory()}
          
          <button className="reset-button" onClick={resetGame}>
            New Game
          </button>
        </div>
      </div>
      {renderPromotionOptions()}
    </div>
  );
}

export default App;
